/**
 * Markdown rendering for chat bubbles, with skeleton placeholders for block elements
 * that have not finished streaming.
 *
 * Prose keeps streaming word by word: it reflows only downwards and reads fine
 * half-written. Block constructs are different — a table's column widths depend on
 * cells that haven't arrived yet, so re-parsing it on every frame makes the bubble
 * jump around, and a long table draws itself row by row. Those blocks are held back:
 * while one is still open at the end of the received text it renders as a dim
 * skeleton, and the real element appears in one step once the block is complete.
 *
 * A finished block either becomes plain HTML (tables, ordinary code fences — marked
 * renders these directly, no mount step needed) or, for kinds backed by a real JS
 * library (charts, diagrams), a `MessageSegment` of its own so message-bubble.vue can
 * mount a live component instead of showing the raw fenced source. Splitting the
 * message into segments — rather than one opaque HTML blob — is what lets those
 * components survive later re-renders: a chart's segment key only changes if its own
 * source text changes, so Vue reuses the mounted ApexCharts/Mermaid instance while
 * later text keeps streaming in after it, instead of tearing it down every frame.
 *
 * Adding a new deferred kind that renders as plain markdown once complete (no JS
 * mount) means adding its info string to `DEFERRED_FENCE_LANGS` only. Adding one that
 * needs a live component (like charts/diagrams/maps below) also means adding it to
 * `COMPONENT_FENCE_LANGS` and giving message-bubble.vue a component for that kind.
 */
/** `end` value meaning "this block runs to the end of the text received so far". */
var OPEN = -1;
// Fenced blocks whose content is a rendered figure rather than text. Plain code
// fences are not listed: code reads fine while it streams.
var DEFERRED_FENCE_LANGS = [
    'mermaid',
    'chart',
    'map',
    // Deep mode: a reference to a stored table/chart/download, fetched by artifact-block.vue.
    'artifact',
    'graph',
    'diagram',
    'plantuml',
    'vega',
    'vega-lite',
];
// Of the kinds above, the ones that get mounted as a live component once complete
// (see the file header). Anything deferred but not listed here still gets the
// skeleton while open, then just renders as an ordinary fenced code block.
var COMPONENT_FENCE_LANGS = ['mermaid', 'chart', 'map', 'artifact'];
var FENCE_RE = /^\s{0,3}(`{3,}|~{3,})\s*([^\s`]*)/;
/**
 * True if `lang` (the fence's info string so far) could still turn into a deferred
 * kind once more of it streams in — e.g. 'cha' while '```chart' is still being typed
 * out token by token. Only meaningful while the opening fence line itself hasn't
 * finished yet (see FENCE_KIND.scan); once it has, the lang is final and this isn't
 * needed.
 */
function isPossibleDeferredLang(lang) {
    if (!lang)
        return true;
    for (var i = 0; i < DEFERRED_FENCE_LANGS.length; i++) {
        if (DEFERRED_FENCE_LANGS[i].indexOf(lang) === 0)
            return true;
    }
    return false;
}
// A finished delimiter row: at least one run of dashes, only table punctuation.
var DELIMITER_RE = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;
// The same row still being typed out ("|", "| ---", "| --- | -").
var PARTIAL_DELIMITER_RE = /^\s*\|[-:|\s]*$/;
function startsWithPipe(line) {
    return line.trim().charAt(0) === '|';
}
function isTableRow(line) {
    return line.trim() !== '' && line.indexOf('|') !== -1;
}
/** True when nothing but blank lines is left from `from` onwards. */
function isOnlyBlank(lines, from) {
    for (var i = from; i < lines.length; i++) {
        if (lines[i].trim() !== '')
            return false;
    }
    return true;
}
/** Cell count of a header row, so the skeleton has roughly the right shape. */
function countColumns(headerLine) {
    var cells = headerLine.trim().replace(/^\|/, '').replace(/\|$/, '').split('|');
    return clamp(cells.length, 1, 8);
}
function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}
function repeat(html, times) {
    var out = '';
    for (var i = 0; i < times; i++)
        out += html;
    return out;
}
// Fixed, so the skeleton never grows as rows arrive — a placeholder that resizes is
// the jitter this exists to remove, and the real row count is unknowable until the
// table is complete anyway.
var SKELETON_ROWS = 4;
function tableSkeleton(columns) {
    var cells = repeat('<span class="mds-cell"></span>', columns);
    return ('<div class="md-skeleton md-skeleton-table" aria-hidden="true">' +
        '<div class="mds-row mds-head">' + cells + '</div>' +
        repeat('<div class="mds-row">' + cells + '</div>', SKELETON_ROWS) +
        '</div>');
}
function figureSkeleton() {
    return '<div class="md-skeleton md-skeleton-figure" aria-hidden="true"></div>';
}
var TABLE_KIND = {
    name: 'table',
    scan: function (lines, i) {
        var header = lines[i];
        if (!isTableRow(header))
            return null;
        var lastIndex = lines.length - 1;
        // Header row with nothing after it yet — without the delimiter row marked
        // would render it as a paragraph of pipes, which is exactly the flicker
        // this avoids.
        if (isOnlyBlank(lines, i + 1)) {
            return startsWithPipe(header)
                ? { end: OPEN, placeholder: tableSkeleton(countColumns(header)) }
                : null;
        }
        var next = lines[i + 1];
        var isDelimiter = DELIMITER_RE.test(next) || (i + 1 === lastIndex && PARTIAL_DELIMITER_RE.test(next));
        if (!isDelimiter)
            return null;
        var j = i + 2;
        while (j < lines.length && isTableRow(lines[j]))
            j++;
        // Rows are only closed by something that isn't a row. Trailing blank lines
        // don't count while streaming: every row ends in a newline, so treating that
        // as the end would render the table again after each row — the row-by-row
        // draw this is meant to replace.
        if (!isOnlyBlank(lines, j))
            return { end: j, placeholder: null };
        return { end: OPEN, placeholder: tableSkeleton(countColumns(header)) };
    },
};
var FENCE_KIND = {
    name: 'fence',
    scan: function (lines, i) {
        var opening = FENCE_RE.exec(lines[i]);
        if (!opening)
            return null;
        var marker = opening[1];
        var lang = opening[2].toLowerCase();
        var j = i + 1;
        while (j < lines.length) {
            var closing = FENCE_RE.exec(lines[j]);
            if (closing && closing[1].charAt(0) === marker.charAt(0) && closing[1].length >= marker.length) {
                return { end: j + 1, placeholder: null };
            }
            j++;
        }
        // Unterminated. While the opening fence line is itself still the last line
        // received, its info string may not be done streaming in yet — '```chart' is
        // '```cha' for a moment first — so a lang that could still complete into a
        // deferred one is treated as deferred too, rather than briefly rendering the
        // fence (and whatever body text follows it) as plain text until the word
        // resolves. Once a later line has arrived, the info string is final and the
        // exact check applies: a figure gets a placeholder, plain code streams as before.
        var openingLineStillGrowing = i === lines.length - 1;
        var deferred = openingLineStillGrowing
            ? isPossibleDeferredLang(lang)
            : DEFERRED_FENCE_LANGS.indexOf(lang) !== -1;
        return { end: OPEN, placeholder: deferred ? figureSkeleton() : null };
    },
};
// Fences first: a table drawn inside a code block is text, not a table.
var BLOCK_KINDS = [FENCE_KIND, TABLE_KIND];
/** Cheap, stable string hash (djb2) so a component segment's key only changes when its own source does. */
function hashText(text) {
    var hash = 5381;
    for (var i = 0; i < text.length; i++) {
        hash = ((hash << 5) + hash + text.charCodeAt(i)) | 0;
    }
    return (hash >>> 0).toString(36);
}
/**
 * Splits a message body into render segments. While `streaming`, an unfinished block
 * at the tail is replaced by its skeleton and nothing after it is parsed yet (it
 * hasn't arrived). A finished chart/mermaid fence becomes its own `ComponentSegment`
 * with a content-derived key, whether or not the message is still streaming — those
 * need a real component mounted, not just markdown. An unfinished chart/mermaid fence
 * in a *final* render (a persisted reply that never closed its own fence) still
 * becomes a `ComponentSegment`, spec truncated as-is — the component's own
 * parse/render failure path shows a "couldn't render" notice, which beats dumping
 * raw JSON/Mermaid source on the user as a naked paragraph. Everything else is plain
 * HTML, merged into as few segments as possible.
 */
export function renderMessageSegments(content, streaming) {
    if (!content)
        return [];
    var lines = content.split('\n');
    var segments = [];
    var htmlStart = 0;
    var htmlSeq = 0;
    var componentSeq = 0;
    function flushHtml(end) {
        if (end <= htmlStart) {
            htmlStart = end;
            return;
        }
        var chunk = lines.slice(htmlStart, end).join('\n');
        htmlStart = end;
        if (chunk.trim())
            segments.push({ kind: 'html', key: 'h' + htmlSeq++, html: marked.parse(chunk) });
    }
    var i = 0;
    while (i < lines.length) {
        var scan = null;
        var isFence = false;
        for (var k = 0; k < BLOCK_KINDS.length; k++) {
            var result = BLOCK_KINDS[k].scan(lines, i);
            if (result) {
                scan = result;
                isFence = BLOCK_KINDS[k] === FENCE_KIND;
                break;
            }
        }
        if (!scan) {
            i++;
            continue;
        }
        if (scan.end === OPEN) {
            // Streaming with a skeleton: show it and stop — nothing after an open
            // block has fully arrived yet.
            if (streaming && scan.placeholder) {
                flushHtml(i);
                segments.push({ kind: 'html', key: 'h' + htmlSeq++, html: scan.placeholder });
                htmlStart = lines.length;
                break;
            }
            // Final render that still finds an open fence: a malformed tail from an
            // older saved session, or a model reply that never closed its own fence
            // (small/local models occasionally cut a chart/mermaid block short). A
            // component fence still goes to its component — malformed JSON/Mermaid
            // source fails to parse/render there and shows the same "couldn't render"
            // fallback a bad-but-complete spec would, which beats dumping raw
            // JSON/Mermaid syntax on the user as a naked paragraph. An open ordinary
            // code fence has no such fallback to reach for, so it still falls through
            // to the closing flush and renders as plain text, same as the rest of
            // the message.
            if (!streaming && isFence) {
                var opening = FENCE_RE.exec(lines[i]);
                var lang = opening ? opening[2].toLowerCase() : '';
                if (COMPONENT_FENCE_LANGS.indexOf(lang) !== -1) {
                    flushHtml(i);
                    var spec = lines.slice(i + 1).join('\n');
                    segments.push({
                        kind: lang,
                        key: lang + '-' + componentSeq++ + '-' + hashText(spec),
                        spec: spec,
                    });
                    htmlStart = lines.length;
                }
            }
            break;
        }
        if (isFence) {
            var opening = FENCE_RE.exec(lines[i]);
            var lang = opening ? opening[2].toLowerCase() : '';
            if (lang === 'map' && streaming) {
                // The model only names the map (a map_id); the server swaps in the points when
                // the answer is complete. Until then the finished block is just a placeholder,
                // never a half-empty map.
                flushHtml(i);
                segments.push({ kind: 'html', key: 'h' + htmlSeq++, html: figureSkeleton() });
                htmlStart = scan.end;
            }
            else if (COMPONENT_FENCE_LANGS.indexOf(lang) !== -1) {
                flushHtml(i);
                var spec = lines.slice(i + 1, scan.end - 1).join('\n');
                segments.push({
                    kind: lang,
                    key: lang + '-' + componentSeq++ + '-' + hashText(spec),
                    spec: spec,
                });
                htmlStart = scan.end;
            }
        }
        i = scan.end;
    }
    flushHtml(lines.length);
    return segments;
}
